import React from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import RenderCategories from "../../components/Dashboard/Talent/Card/UserCardDesign/RenderCategories";
import YogaTalent from "../../components/assets/whoPerforms/Yoga.png";
import CookingTalent from "../../components/assets/whoPerforms/cooking.png";
import SaxophoneTalent from "../../components/assets/whoPerforms/sexopone_talent.png";
import ClappingTalent from "../../components/assets/whoPerforms/clapping_hand.png";

/** ── Netflix-ish tokens ─────────────────────────────────────────────── */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.78)",
  red: "#E50914",
  redHover: "#b20710",
  border: "rgba(255,255,255,0.12)",
};

/** ── Styled Components ──────────────────────────────────────────────── */
const SectionRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "relative",
  backgroundColor: COLORS.bg,
  padding: theme.spacing(10, 2),
  overflow: "hidden",
  // subtle cinematic glows
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(1000px 520px at 8% 0%, rgba(229,9,20,0.12), transparent 60%), radial-gradient(900px 480px at 100% 10%, rgba(255,255,255,0.06), transparent 60%)",
    pointerEvents: "none",
  },
}));

const TalentCard = styled(motion.div)(({ theme }) => ({
  backgroundColor: COLORS.panel,
  borderRadius: 16,
  border: `1px solid ${COLORS.border}`,
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  height: "100%",
  transition: "transform .25s ease, border-color .25s ease, box-shadow .25s ease",
  boxShadow: "0 14px 28px rgba(0,0,0,0.45)",
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: COLORS.red,
    boxShadow: "0 18px 36px rgba(229,9,20,0.18), 0 14px 28px rgba(0,0,0,0.55)",
  },
}));

const TalentImage = styled("img")(({ theme }) => ({
  width: "88%",
  height: "auto",
  objectFit: "cover",
  borderRadius: 12,
  boxShadow:
    "0 10px 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset",
  [theme.breakpoints.down("sm")]: {
    width: 120,
  },
}));

const Paragraph = styled(Typography)(() => ({
  fontSize: "1rem",
  lineHeight: 1.7,
  color: COLORS.textMuted,
}));

/** ── Data ───────────────────────────────────────────────────────────── */
const imageCards = [
  {
    src: SaxophoneTalent,
    alt: "Musician performing live",
    label: "A musician performs for a café audience",
  },
  {
    src: YogaTalent,
    alt: "Dance instructor",
    label: "A dance instructor teaches an online class",
  },
  {
    src: ClappingTalent,
    alt: "Crafts seller",
    label: "A woman sells handmade products online",
  },
  {
    src: CookingTalent,
    alt: "Chef workshop",
    label: "A chef hosts a members-only cooking workshop",
  },
];

const WhoPerformsSection: React.FC = () => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <SectionRoot>
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 }, position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Title + Tagline */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant={smDown ? "h5" : "h4"}
              sx={{
                fontWeight: 900,
                letterSpacing: "-0.02em",
                color: COLORS.text,
                textShadow: "0 1px 0 rgba(0,0,0,0.4)",
              }}
            >
              WHO PERFORMS
            </Typography>
            <Box
              sx={{
                mt: 1,
                height: 3,
                width: 72,
                borderRadius: 999,
                background: COLORS.red,
                boxShadow: "0 8px 20px rgba(229,9,20,0.35)",
              }}
            />
          </Box>

          <Typography
            variant={smDown ? "subtitle1" : "h6"}
            sx={{ color: COLORS.textMuted, fontWeight: 700, mb: 3 }}
          >
            For Talents: Your Stage, Your Market.
          </Typography>

          <Paragraph sx={{ mb: 1.5 }}>
            On StageLink, you’re not just listing a service —{" "}
            <Box component="span" sx={{ color: COLORS.text, fontWeight: 800 }}>
              you're hosting an experience.
            </Box>
          </Paragraph>
          <Paragraph sx={{ mb: 3 }}>
            StageLink gives you the real-time stage to engage fans, mentor students,
            or help clients instantly across various categories.
          </Paragraph>

          {/* Categories strip (kept as-is) */}
          <RenderCategories />

          {/* Cards */}
          <Grid container spacing={3} sx={{ my: 4 }}>
            {imageCards.map((card, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <TalentCard whileHover={{ scale: 1.03 }} transition={{ duration: 0.25 }}>
                  <TalentImage src={card.src} alt={card.alt} />
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{ mt: 2, color: COLORS.text, fontWeight: 700 }}
                  >
                    {card.label}
                  </Typography>
                </TalentCard>
              </Grid>
            ))}
          </Grid>

          {/* Copy */}
          <Box maxWidth="md">
            <Paragraph sx={{ mb: 1.5 }}>
              Talents can engage audiences both{" "}
              <Box component="span" sx={{ color: COLORS.text, fontWeight: 800 }}>
                on-screen
              </Box>{" "}
              and{" "}
              <Box component="span" sx={{ color: COLORS.text, fontWeight: 800 }}>
                on-site
              </Box>
              , thanks to{" "}
              <Box component="span" sx={{ color: COLORS.text, fontWeight: 800 }}>
                StageLink™
              </Box>
              .
            </Paragraph>
            <Paragraph sx={{ mb: 1.5 }}>
              With our open platform features, talents can even reach and engage fans
              through their favorite social apps — while growing their presence on StageLink.
            </Paragraph>
            <Paragraph sx={{ mb: 0 }}>
              You can easily be discovered and connect with sponsors like{" "}
              <Box component="span" sx={{ color: COLORS.text, fontWeight: 800 }}>
                brands, NGOs
              </Box>
              , and campaign organizers.
            </Paragraph>

            <Typography
              variant={smDown ? "subtitle1" : "h6"}
              sx={{ color: COLORS.text, fontWeight: 800, mt: 3 }}
            >
              ✨ Whether you’re just starting out or already a star — <strong>we help you rise.</strong>
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </SectionRoot>
  );
};

export default WhoPerformsSection;
